import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { NavUser } from "@/components/nav-user"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  LayoutDashboardIcon,
  RouteIcon as TripIcon,
  FileTextIcon,
  ScrollTextIcon,
  Settings2Icon,
  CircleHelpIcon,
  UsersIcon,
  BanknoteIcon,
  GlobeIcon,
  MapPinIcon,
  BuildingIcon,
  RouteIcon,
  ClipboardListIcon,
  PackageIcon,
  BoxesIcon,
  ChevronDownIcon,
  CreditCardIcon,
  FileStackIcon,
  CoinsIcon,
  TruckIcon,
  CarIcon,
  UserIcon,
  LinkIcon,
  ShieldIcon,
  HistoryIcon,
  SendIcon,
  UsersRoundIcon,
  MapIcon,
  Globe2Icon,
  LayersIcon,
  UploadIcon,
  HandshakeIcon,
  FlagIcon,
  BarChart2Icon,
  GaugeIcon,
  ReceiptIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { usePermissions } from "@/hooks/use-permissions"
import logo from "@/assets/logo.png"

// ─── Helpers ─────────────────────────────────────────────────────────────────

function groupIsActive(items: { url: string }[], pathname: string): boolean {
  return items.some(
    item => pathname === item.url || pathname.startsWith(item.url + "/")
  )
}

// Static list of sistema URLs for route-matching (navSistema is built inside
// the component because it conditionally includes admin-only items).
const navSistemaUrls = ["/users", "/system/audit", "/system/roles", "/system/kpi", "/doc-types", "/shipment-doc-types", "/currencies", "/pay-concepts", "/smart-gt/import-logs"]

// ─── Nav arrays ───────────────────────────────────────────────────────────────

const navGeo = [
  { title: "Países",                   url: "/geo/countries", icon: <GlobeIcon /> },
  { title: "Provincias",               url: "/geo/states",    icon: <MapPinIcon /> },
  { title: "Ciudades y Trayectorias",  url: "/geo/cities",    icon: <BuildingIcon /> },
  { title: "Rutas",                    url: "/geo/routes",    icon: <RouteIcon /> },
]

const navTrips = [
  { title: "Viajes",           url: "/trips",              icon: <TripIcon /> },
  { title: "Tipos de servicio", url: "/trips/service-types", icon: <ClipboardListIcon /> },
]

const navCargasLineas = [
  { title: "Nacional",      url: "/cargos/nacional",      icon: <MapIcon /> },
  { title: "Internacional", url: "/cargos/internacional", icon: <Globe2Icon /> },
  { title: "Batea",         url: "/cargos/batea",         icon: <LayersIcon /> },
]

const navCargos = [
  { title: "Productos",      url: "/cargos/products", icon: <PackageIcon /> },
  { title: "Tipos de carga", url: "/cargos/types",    icon: <BoxesIcon /> },
]

// All cargas-related URLs used for group-level active detection
const navCargosGroupUrls = ["/shipments", "/cargos/products", "/cargos/types", "/cargos/nacional", "/cargos/internacional", "/cargos/batea", "/batea/final-destinations", "/cargos/informes", "/shipments/billing"]

const navFlota = [
  { title: "Tipos de unidad", url: "/unit-types", icon: <TruckIcon /> },
  { title: "Unidades",        url: "/units",      icon: <CarIcon /> },
  { title: "Asignaciones",    url: "/mobiles",    icon: <LinkIcon /> },
]

const navRelaciones = [
  { title: "Partes",     url: "/parties",               icon: <UsersRoundIcon /> },
  { title: "Clientes",   url: "/clients",               icon: <BuildingIcon /> },
  { title: "Contratos",  url: "/relations/contracts",   icon: <HandshakeIcon /> },
]

const navChoferes = [
  { title: "Choferes", url: "/drivers",         icon: <UserIcon /> },
  { title: "Remitos",  url: "/drivers/remitos",  icon: <FileTextIcon /> },
]

const navRelacionesAll = [
  ...navRelaciones,
  ...navChoferes,
]

const navSmartGT = [
  { title: "CRTs",   url: "/smart-gt",         icon: <FileTextIcon /> },
  { title: "Chofer", url: "/smart-gt/chofer",   icon: <UserIcon /> },
]


const navAyuda = [
  { title: "Documentación", url: "/docs",      icon: <FileTextIcon /> },
  { title: "Guía de uso",   url: "/help",      icon: <CircleHelpIcon /> },
  { title: "Changelog",     url: "/changelog", icon: <ScrollTextIcon /> },
]

// ─── Component ────────────────────────────────────────────────────────────────

export function AppSidebar({
  user,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  user: { name: string; email: string; avatar: string }
}) {
  const { pathname } = useLocation()
  const { can } = usePermissions()

  const canSeeGeo        = can("visit", "geo")
  const canSeeTrips      = can("visit", "trips")
  const canSeeCargos     = can("visit", "cargo") || can("visit", "shipments")
  const canSeeFlota      = can("visit", "units") || can("visit", "mobiles")
  const canSeeRelaciones = can("visit", "drivers") || can("visit", "clients") || can("visit", "parties") || can("visit", "contracts")
  const canSeeConfig     = can("visit", "config") || can("visit", "users") || can("visit", "roles") || can("visit", "audit")
  const canSeeBatea      = canSeeCargos

  // Filter individual items based on permissions
  const visibleCargos     = navCargos
  const visibleFlota      = navFlota.filter(i => i.url !== "/mobiles" || can("visit", "mobiles"))
  const visibleRelaciones = navRelaciones.filter(i => {
    if (i.url === "/parties")             return can("visit", "parties")
    if (i.url === "/relations/contracts") return can("visit", "contracts")
    return true
  })

  const [geoOpen,         setGeoOpen]         = React.useState(() => groupIsActive(navGeo,        pathname))
  const [tripsOpen,       setTripsOpen]        = React.useState(() => groupIsActive(navTrips,      pathname))
  const [cargosOpen,      setCargosOpen]       = React.useState(() => navCargosGroupUrls.some(u => pathname === u || pathname.startsWith(u + "/")))

  const [flotaOpen,       setFlotaOpen]        = React.useState(() => groupIsActive(navFlota,      pathname))
  const [relacionesOpen,  setRelacionesOpen]   = React.useState(() => groupIsActive(navRelacionesAll, pathname))
  const [choferesOpen,    setChoferesOpen]     = React.useState(() => groupIsActive(navChoferes, pathname))
  const [smartGTOpen,     setSmartGTOpen]      = React.useState(() => groupIsActive(navSmartGT,    pathname))

  const [sistemaOpen,     setSistemaOpen]      = React.useState(() => navSistemaUrls.some(u => pathname === u || pathname.startsWith(u + "/")))
  const [ayudaOpen,       setAyudaOpen]        = React.useState(() => groupIsActive(navAyuda,      pathname))

  // Auto-open the relevant group when the user navigates to a new route.
  React.useEffect(() => {
    if (groupIsActive(navGeo,        pathname)) setGeoOpen(true)
    if (groupIsActive(navTrips,      pathname)) setTripsOpen(true)
    if (navCargosGroupUrls.some(u => pathname === u || pathname.startsWith(u + "/"))) setCargosOpen(true)

    if (groupIsActive(navFlota,      pathname)) setFlotaOpen(true)
    if (groupIsActive(navRelacionesAll, pathname)) setRelacionesOpen(true)
    if (groupIsActive(navChoferes,      pathname)) setChoferesOpen(true)
    if (groupIsActive(navSmartGT,    pathname)) setSmartGTOpen(true)

    if (groupIsActive(navAyuda,      pathname)) setAyudaOpen(true)
    if (navSistemaUrls.some(u => pathname === u || pathname.startsWith(u + "/"))) setSistemaOpen(true)
  }, [pathname])

  const navSistema = [
    ...(can("visit", "users")  ? [{ title: "Usuarios",              url: "/users",                 icon: <UsersIcon /> }]    : []),
    ...(can("visit", "roles")  ? [{ title: "Roles y permisos",      url: "/system/roles",          icon: <ShieldIcon /> }]   : []),
    ...(can("visit", "audit")  ? [{ title: "Auditoría",             url: "/system/audit",          icon: <HistoryIcon /> }]  : []),
    ...(can("visit", "users")  ? [{ title: "KPI",                   url: "/system/kpi",            icon: <GaugeIcon /> }]    : []),
    ...(can("visit", "config") ? [
      { title: "Tipos de documento", url: "/doc-types",    icon: <CreditCardIcon /> },
    ] : []),
    ...(can("visit", "shipmentDocTypes") ? [
      { title: "Docs. de viaje",     url: "/shipment-doc-types", icon: <FileStackIcon /> },
    ] : []),
    ...(can("visit", "config") ? [
      { title: "Monedas",            url: "/currencies",   icon: <CoinsIcon /> },
      { title: "Conceptos de pago",  url: "/pay-concepts", icon: <BanknoteIcon /> },
      { title: "Configuraciones",     url: "/system/settings", icon: <Settings2Icon /> },
    ] : []),
    ...(can("visit", "audit")  ? [{ title: "Log de importación CRT", url: "/smart-gt/import-logs", icon: <UploadIcon /> }]  : []),
  ]

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="data-[slot=sidebar-menu-button]:p-1.5!"
              render={<Link to="/dashboard" />}
            >
              <img src={logo} alt="CDC" className="h-6 w-auto" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="gap-0">

        {/* ── Dashboard ──────────────────────────────────────────────────── */}
        <SidebarMenu className="px-2 py-0.5">
          <SidebarMenuItem>
            <SidebarMenuButton render={<Link to="/dashboard" />}>
              <LayoutDashboardIcon />
              <span className="text-sm">Dashboard</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* ── Trips ──────────────────────────────────────────────────────── */}
        {canSeeTrips && (
          <SidebarGroup className="px-2 py-0.5">
            <div className="relative">
              {groupIsActive(navTrips, pathname) && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
              )}
              <button type="button" onClick={() => setTripsOpen(v => !v)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                  groupIsActive(navTrips, pathname)
                    ? "text-sidebar-foreground bg-sidebar-accent/10"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
                )}>
                <span>Viajes</span>
                <ChevronDownIcon className={cn("size-4 transition-transform duration-200", tripsOpen ? "" : "-rotate-90")} />
              </button>
            </div>
            {tripsOpen && (
              <SidebarGroupContent className="mt-1">
                <SidebarMenu className="gap-0.5">
                  {navTrips.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                        {item.icon}<span className="text-xs">{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        {/* ── Cargo items ────────────────────────────────────────────────── */}
        {canSeeCargos && (
          <SidebarGroup className="px-2 py-0.5">
            <div className="relative">
              {navCargosGroupUrls.some(u => pathname === u || pathname.startsWith(u + "/")) && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
              )}
              <button type="button" onClick={() => setCargosOpen(v => !v)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                  navCargosGroupUrls.some(u => pathname === u || pathname.startsWith(u + "/"))
                    ? "text-sidebar-foreground bg-sidebar-accent/10"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
                )}>
                <span>Cargas</span>
                <ChevronDownIcon className={cn("size-4 transition-transform duration-200", cargosOpen ? "" : "-rotate-90")} />
              </button>
            </div>
            {cargosOpen && (
              <SidebarGroupContent className="mt-1">
                <SidebarMenu className="gap-0.5">

                  {/* Todas — only for roles with shipments access */}
                  {can("visit", "shipments") && (
                    <SidebarMenuItem>
                      <SidebarMenuButton size="sm" isActive={pathname === "/shipments"} render={<Link to="/shipments" />} className="pl-6 text-xs">
                        <SendIcon /><span className="text-xs">Todas</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                  {/* Facturación */}
                  {can("visit", "shipments") && (
                    <SidebarMenuItem>
                      <SidebarMenuButton size="sm" isActive={pathname === "/shipments/billing"} render={<Link to="/shipments/billing" />} className="pl-6 text-xs">
                        <ReceiptIcon /><span className="text-xs">Facturación</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                  {/* Internacional */}
                  {can("visit", "cargo") && (
                    <SidebarMenuItem>
                      <SidebarMenuButton size="sm" isActive={pathname === "/cargos/internacional"} render={<Link to="/cargos/internacional" />} className="pl-6 text-xs">
                        <Globe2Icon /><span className="text-xs">Internacional</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                  {/* Productos + Tipos de carga */}
                  {visibleCargos.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                        {item.icon}<span className="text-xs">{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}

                  {/* Destinos finales (Batea) */}
                  {canSeeBatea && (
                    <SidebarMenuItem>
                      <SidebarMenuButton size="sm" isActive={pathname === "/batea/final-destinations"} render={<Link to="/batea/final-destinations" />} className="pl-6 text-xs">
                        <FlagIcon /><span className="text-xs">Destinos finales (Batea)</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                  {/* Informes */}
                  {can("visit", "cargo") && (
                    <SidebarMenuItem>
                      <SidebarMenuButton size="sm" isActive={pathname === "/cargos/informes"} render={<Link to="/cargos/informes" />} className="pl-6 text-xs">
                        <BarChart2Icon /><span className="text-xs">Informes</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}

                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        {/* ── Fleet ──────────────────────────────────────────────────────── */}
        {canSeeFlota && (
          <SidebarGroup className="px-2 py-0.5">
            <div className="relative">
              {groupIsActive(navFlota, pathname) && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
              )}
              <button type="button" onClick={() => setFlotaOpen(v => !v)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                  groupIsActive(navFlota, pathname)
                    ? "text-sidebar-foreground bg-sidebar-accent/10"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
                )}>
                <span>Flota</span>
                <ChevronDownIcon className={cn("size-4 transition-transform duration-200", flotaOpen ? "" : "-rotate-90")} />
              </button>
            </div>
            {flotaOpen && (
              <SidebarGroupContent className="mt-1">
                <SidebarMenu className="gap-0.5">
                  {visibleFlota.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                        {item.icon}<span className="text-xs">{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        {/* ── Relaciones ────────────────────────────────────────────── */}
        {canSeeRelaciones && (
          <SidebarGroup className="px-2 py-0.5">
            <div className="relative">
              {groupIsActive(navRelaciones, pathname) && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
              )}
              <button type="button" onClick={() => setRelacionesOpen(v => !v)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                  groupIsActive(navRelaciones, pathname)
                    ? "text-sidebar-foreground bg-sidebar-accent/10"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
                )}>
                <span>Relaciones</span>
                <ChevronDownIcon className={cn("size-4 transition-transform duration-200", relacionesOpen ? "" : "-rotate-90")} />
              </button>
            </div>
            {relacionesOpen && (
              <SidebarGroupContent className="mt-1">
                <SidebarMenu className="gap-0.5">
                  {visibleRelaciones.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                        {item.icon}<span className="text-xs">{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}

                  {/* ── Choferes sub-group ── */}
                  <SidebarMenuItem>
                    <button
                      type="button"
                      onClick={() => setChoferesOpen(v => !v)}
                      className={cn(
                        "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-medium transition-colors ml-4",
                        groupIsActive(navChoferes, pathname)
                          ? "bg-sidebar-accent/50 text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <UserIcon className="size-3" />
                        <span className="text-xs">Choferes</span>
                      </span>
                      <ChevronDownIcon className={cn("size-3 transition-transform duration-200", choferesOpen ? "" : "-rotate-90")} />
                    </button>
                    {choferesOpen && (
                      <SidebarMenu className="gap-0.5 mt-0.5">
                        {navChoferes.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-12 text-xs">
                              {item.icon}<span className="text-xs">{item.title}</span>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    )}
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        {/* ── SmartGT ────────────────────────────────────────────────── */}
        {/* ── SmartGT ──────────────────────────────────────────────────────────────────────────────────────────────────────── */}
        <SidebarGroup className="px-2 py-0.5">
          <div className="relative">
            {groupIsActive(navSmartGT, pathname) && (
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
            )}
            <button type="button" onClick={() => setSmartGTOpen(v => !v)}
              className={cn(
                "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                groupIsActive(navSmartGT, pathname)
                  ? "text-sidebar-foreground bg-sidebar-accent/10"
                  : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
              )}>
              <span>SmartGT</span>
              <ChevronDownIcon className={cn("size-4 transition-transform duration-200", smartGTOpen ? "" : "-rotate-90")} />
            </button>
          </div>
          {smartGTOpen && (
            <SidebarGroupContent className="mt-1">
              <SidebarMenu className="gap-0.5">
                {navSmartGT.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                      {item.icon}<span className="text-xs">{item.title}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          )}
        </SidebarGroup>

        {/* ── Configuración + Ayuda — al fondo ──────────────────────────── */}
        <div className="mt-auto">

          {/* ── Geography ─────────────────────────────────────────────────── */}
          {canSeeGeo && (
            <SidebarGroup className="px-2 py-0.5">
              <div className="relative">
                {groupIsActive(navGeo, pathname) && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
                )}
                <button type="button" onClick={() => setGeoOpen(v => !v)}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                    groupIsActive(navGeo, pathname)
                      ? "text-sidebar-foreground bg-sidebar-accent/10"
                      : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
                  )}>
                  <span className="flex items-center gap-2"><MapIcon className="size-4" />Geografía</span>
                  <ChevronDownIcon className={cn("size-4 transition-transform duration-200", geoOpen ? "" : "-rotate-90")} />
                </button>
              </div>
              {geoOpen && (
                <SidebarGroupContent className="mt-1">
                  <SidebarMenu className="gap-0.5">
                    {navGeo.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                          {item.icon}<span className="text-xs">{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          )}

          {/* ── Sistema (admin only) ────────────────────────────────────────── */}
          {canSeeConfig && (
            <SidebarGroup className="px-2 py-0.5">
              <div className="relative">
                {navSistemaUrls.some(u => pathname === u || pathname.startsWith(u + "/")) && (
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
                )}
                <button type="button" onClick={() => setSistemaOpen(v => !v)}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                    navSistemaUrls.some(u => pathname === u || pathname.startsWith(u + "/"))
                      ? "text-sidebar-foreground bg-sidebar-accent/10"
                      : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
                  )}>
                  <span className="flex items-center gap-2"><ShieldIcon className="size-4" />Sistema</span>
                  <ChevronDownIcon className={cn("size-4 transition-transform duration-200", sistemaOpen ? "" : "-rotate-90")} />
                </button>
              </div>
              {sistemaOpen && (
                <SidebarGroupContent className="mt-1">
                  <SidebarMenu className="gap-0.5">
                    {navSistema.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                          {item.icon}<span className="text-xs">{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          )}

          <SidebarGroup className="px-2 py-0.5">
            <div className="relative">
              {groupIsActive(navAyuda, pathname) && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-sidebar-accent rounded-l-md"></div>
              )}
              <button type="button" onClick={() => setAyudaOpen(v => !v)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-1.5 text-xs font-bold transition-all rounded-md",
                  groupIsActive(navAyuda, pathname)
                    ? "text-sidebar-foreground bg-sidebar-accent/10"
                    : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/5"
                )}>
                <span className="flex items-center gap-2"><CircleHelpIcon className="size-4" />Ayuda</span>
                <ChevronDownIcon className={cn("size-4 transition-transform duration-200", ayudaOpen ? "" : "-rotate-90")} />
              </button>
            </div>
            {ayudaOpen && (
              <SidebarGroupContent className="mt-1">
                <SidebarMenu className="gap-0.5">
                  {navAyuda.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton size="sm" isActive={pathname === item.url} render={<Link to={item.url} />} className="pl-6 text-xs">
                        {item.icon}<span className="text-xs">{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            )}
          </SidebarGroup>
        </div>

      </SidebarContent>

      <SidebarFooter>
        <NavUser user={user} />
      </SidebarFooter>
    </Sidebar>
  )
}
